import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AddressBook = ({ addresses, onAddAddress, onEditAddress, onDeleteAddress, onSetDefault }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState({
    type: 'home',
    firstName: '',
    lastName: '',
    company: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (editingAddress) {
      onEditAddress(editingAddress?.id, formData);
      setEditingAddress(null);
    } else {
      onAddAddress(formData);
      setShowAddForm(false);
    }
    setFormData({
      type: 'home',
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
      phone: ''
    });
  };

  const handleEdit = (address) => {
    setFormData(address);
    setEditingAddress(address);
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingAddress(null);
    setFormData({
      type: 'home',
      firstName: '',
      lastName: '',
      company: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United States',
      phone: ''
    });
  };

  const getAddressTypeIcon = (type) => {
    switch (type) {
      case 'home':
        return 'Home';
      case 'work':
        return 'Building';
      case 'other':
        return 'MapPin';
      default:
        return 'MapPin';
    }
  };

  return (
    <div className="holographic-card p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold command-text">Address Book</h2>
        <Button
          variant="default"
          size="sm"
          onClick={() => setShowAddForm(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add New Address
        </Button>
      </div>
      {/* Add/Edit Address Form */}
      {showAddForm && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-muted/20">
          <h3 className="text-lg font-semibold mb-4">
            {editingAddress ? 'Edit Address' : 'Add New Address'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Address Type</label>
                <select
                  name="type"
                  value={formData?.type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="home">Home</option>
                  <option value="work">Work</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <Input
                label="First Name"
                name="firstName"
                value={formData?.firstName}
                onChange={handleInputChange}
                required
              />
              <Input
                label="Last Name"
                name="lastName"
                value={formData?.lastName}
                onChange={handleInputChange}
                required
              />
            </div>

            <Input
              label="Company (Optional)"
              name="company"
              value={formData?.company}
              onChange={handleInputChange}
            />

            <Input
              label="Address Line 1"
              name="address1"
              value={formData?.address1}
              onChange={handleInputChange}
              required
            />

            <Input
              label="Address Line 2 (Optional)"
              name="address2"
              value={formData?.address2}
              onChange={handleInputChange}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="City"
                name="city"
                value={formData?.city}
                onChange={handleInputChange}
                required
              />
              <Input
                label="State"
                name="state"
                value={formData?.state}
                onChange={handleInputChange}
                required
              />
              <Input
                label="ZIP Code"
                name="zipCode"
                value={formData?.zipCode}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Country"
                name="country"
                value={formData?.country}
                onChange={handleInputChange}
                required
              />
              <Input
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData?.phone}
                onChange={handleInputChange}
              />
            </div>

            <div className="flex space-x-2">
              <Button type="submit" variant="default">
                {editingAddress ? 'Update Address' : 'Save Address'}
              </Button>
              <Button type="button" variant="ghost" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* Address List */}
      <div className="space-y-4">
        {addresses?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="MapPin" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No addresses saved yet</p>
          </div>
        ) : (
          addresses?.map((address) => (
            <div
              key={address?.id}
              className={`border rounded-lg p-4 transition-all duration-200 ${
                address?.isDefault
                  ? 'border-primary bg-primary/5' :'border-border hover:bg-muted/20'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    address?.isDefault ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={getAddressTypeIcon(address?.type)} size={20} />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold capitalize">{address?.type} Address</h3>
                      {address?.isDefault && (
                        <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full border border-primary/30">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-foreground">
                      {address?.firstName} {address?.lastName}
                    </p>
                    {address?.company && (
                      <p className="text-sm text-muted-foreground">{address?.company}</p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {address?.address1}
                      {address?.address2 && `, ${address?.address2}`}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {address?.city}, {address?.state} {address?.zipCode}
                    </p>
                    <p className="text-sm text-muted-foreground">{address?.country}</p>
                    {address?.phone && (
                      <p className="text-sm text-muted-foreground">{address?.phone}</p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  {!address?.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSetDefault(address?.id)}
                      iconName="Star"
                      iconPosition="left"
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(address)}
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteAddress(address?.id)}
                    iconName="Trash2"
                    iconPosition="left"
                    className="text-error hover:text-error"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AddressBook;